import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdScience, MdPlayArrow, MdTimer } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

// Mirrors the server's computeStepStatus() so the UI reflects the same
// startedAt + duration derived active/remaining state without extra calls.
function computeStatus(step) {
    if (!step.startedAt) return { active: false, remainingSeconds: 0 };
    const elapsed = (Date.now() - new Date(step.startedAt).getTime()) / 1000;
    const remainingSeconds = Math.max(0, Math.ceil(step.duration - elapsed));
    return { active: remainingSeconds > 0, remainingSeconds };
}

export default function SensorCalibrationModal({ plcId, entry, sensorName, onClose, onUpdate }) {
    const { t } = useLanguage();
    const [durationInputs, setDurationInputs] = useState(() => {
        const init = {};
        entry.steps.forEach(s => { init[s._id] = s.duration || ''; });
        return init;
    });
    const [startingId, setStartingId] = useState(null);
    const [, setTick] = useState(0);

    // Re-render every second so active/remainingSeconds count down live.
    useEffect(() => {
        const interval = setInterval(() => setTick(v => v + 1), 1000);
        return () => clearInterval(interval);
    }, []);

    const handleStart = async (stepId) => {
        const duration = Number(durationInputs[stepId]);
        if (!Number.isFinite(duration) || duration <= 0) {
            toast.error(t('plc.durationRequired'));
            return;
        }
        try {
            setStartingId(stepId);
            const res = await api.put(`/plc/${plcId}/calibration/sensors/${entry._id}/steps/${stepId}/start`, { duration });
            toast.success(t('plc.calibrationStepStarted'));
            onUpdate(res.data.data);
        } catch (err) {
            toast.error(err.response?.data?.message || t('plc.calibrationStepStartFailed'));
        } finally {
            setStartingId(null);
        }
    };

    return (
        <AnimatePresence>
            <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={(e) => e.target === e.currentTarget && onClose()}>
                <motion.div className="modal" style={{ maxWidth: 520 }} initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.25 }}>

                    <div className="modal-header">
                        <div className="modal-title">
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--success)' }}>
                                <MdScience />
                            </div>
                            {sensorName} — {t('plc.calibrationSteps')}
                        </div>
                        <button className="modal-close" onClick={onClose}><MdClose /></button>
                    </div>

                    <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {entry.steps.map((step, idx) => {
                            const { active, remainingSeconds } = computeStatus(step);
                            return (
                                <div key={step._id} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontWeight: 700, fontSize: 14 }}>{t('plc.step')} {idx + 1}</span>
                                            <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text-muted)' }}>{step._id}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <div style={{
                                                width: 8, height: 8, borderRadius: '50%',
                                                background: active ? 'var(--success)' : '#ef4444',
                                                boxShadow: active ? '0 0 8px var(--success)' : '0 0 8px rgba(239,68,68,0.4)',
                                                transition: 'background 0.4s'
                                            }} />
                                            <span style={{ fontSize: 11, fontWeight: 700, color: active ? 'var(--success)' : '#ef4444' }}>
                                                {active ? t('plc.stepActive') : t('plc.stepInactive')}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                        <input
                                            type="number"
                                            className="form-input form-input-sm"
                                            placeholder={t('plc.durationPlaceholder')}
                                            value={durationInputs[step._id] ?? ''}
                                            onChange={e => setDurationInputs(prev => ({ ...prev, [step._id]: e.target.value }))}
                                            disabled={active}
                                            style={{ width: 100 }}
                                        />
                                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('plc.durationSeconds')}</span>

                                        {active ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', fontWeight: 700, color: 'var(--success)' }}>
                                                <MdTimer />
                                                {remainingSeconds}s
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleStart(step._id)}
                                                className="btn btn-primary btn-sm"
                                                disabled={startingId === step._id}
                                                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}
                                            >
                                                {startingId === step._id ? <span className="spinner spinner-sm" /> : <MdPlayArrow />}
                                                {t('plc.start')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.close')}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
